import React, { useEffect, useState, useRef, useCallback } from "react";
import mixpanel from "mixpanel-browser";
import axios from "axios";
import logo from "../sidebar-logo.png";
import Player, { PlayerWrapper } from "../Components/PlayerNew";
import { observer } from "mobx-react";
import ShareModal from '../Components/ShareModal/index';
import { Share as ShareIcon } from '@mui/icons-material';
import { Link, useHistory, useLocation } from "react-router-dom";
import "./Share.scss";
import FeedSubscribed from "../Pages/DiscoverNew/Components/FeedSubscribed";
import { CircularProgress } from "@mui/material";
// import { getAllFeeds, getLatestFeedArticleFromFeed } from "../audiblogsApi";
import OneGiftLogo from '../Components/OneGiftLogo';
import { Helmet } from 'react-helmet';
import { setLandingPage } from '../utils/trackingParams';
import { publish, subscribe, unsubscribe } from '../PlayerPubSub';
import Loader from '../Components/Loader';

const onPreviousArticle = () => null;
const onNextArticle = () => null;
const onArticleEnded = () => null;

const FEEDS_PER_PAGE = 10;

const SharePlayer = observer(
  ({
    match: {
      params: { filename },
    },
  }) => {
    const [isFullPlayer, updateIsFullPlayer] = useState(false);
    const [currentArticle, setCurrentArticle] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [allFeeds, setAllFeeds] = useState([]);
    const [displayedFeeds, setDisplayedFeeds] = useState([]);
    const [feedsLoading, setFeedsLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [showTranscript, setShowTranscript] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState(null);
    const observer = useRef();
    const loadingRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const currentParams = searchParams.toString();

    // Extract the audio_id (filename after last slash, without extension)
    const fullPath = location.pathname.replace('/share/', '');
    const lastSlashIndex = fullPath.lastIndexOf('/');
    const filenameWithExt = lastSlashIndex >= 0 ? fullPath.substring(lastSlashIndex + 1) : fullPath;
    const audio_id = filenameWithExt.substring(0, filenameWithExt.lastIndexOf('.')) || filenameWithExt;

    // Subscribe to player state changes
    useEffect(() => {
      const handlePlaying = () => {
        console.log("Share page: Playing event received");
        setIsPlaying(true);
      };
      const handlePaused = () => {
        console.log("Share page: Paused event received");
        setIsPlaying(false);
      };

      console.log("Share page: Subscribing to player events");
      subscribe("playing", handlePlaying);
      subscribe("paused", handlePaused);

      return () => {
        console.log("Share page: Unsubscribing from player events");
        unsubscribe("playing", handlePlaying);
        unsubscribe("paused", handlePaused);
      };
    }, []);

    // Add effect to preserve URL parameters
    useEffect(() => {
      // If we have params but they're not in the URL, update the URL
      if (currentParams && !location.search) {
        history.replace({
          pathname: location.pathname,
          search: currentParams
        });
      }
    }, [location, history, currentParams]);

    // Add changeCurrentArticle function
    const changeCurrentArticle = (article) => {
      setCurrentArticle(article);
    };

    const loadMoreFeeds = useCallback(() => {
      if (!feedsLoading && hasMore) {
        const start = page * FEEDS_PER_PAGE;
        const end = start + FEEDS_PER_PAGE;
        const newFeeds = allFeeds.slice(start, end);
        
        if (newFeeds.length > 0) {
          setDisplayedFeeds(prev => [...prev, ...newFeeds]);
          setPage(prev => prev + 1);
          setHasMore(end < allFeeds.length);
        } else {
          setHasMore(false);
        }
      }
    }, [page, allFeeds, feedsLoading, hasMore]);

    // Intersection observer setup
    useEffect(() => {
      if (loadingRef.current && hasMore) {
        observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreFeeds();
          }
        }, { threshold: 0.5 });

        observer.current.observe(loadingRef.current);
      }

      return () => {
        if (observer.current) {
          observer.current.disconnect();
        }
      };
    }, [loadMoreFeeds, hasMore]);

    useEffect(() => {
      // Track page visit with consistent event name
      mixpanel.track('Landing Page Visited', {
        page: 'share',
        filename: audio_id,
        utm_landingpage: 'share'
      });
      setLandingPage('share');
      mixpanel.start_session_recording();

      console.log("Fetching article with audio_id:", audio_id);
      axios
        .get(
          `${process.env.REACT_APP_BACKEND_URL}/api/article/${encodeURIComponent(audio_id)}`
        )
        .then(({ data }) => {
          console.log("Article data received:", data);
          
          // Check if backend returned an error
          if (data.error) {
            console.log("Backend returned error:", data.error);
            setError("Article not found");
            return;
          }
          
          // Check if we have required article data
          if (!data.article_id || !data.title) {
            console.log("Missing required article data");
            setError("Article not found");
            return;
          }
          
          // Format the article data for the player
          const formattedArticle = {
            ...data,
            article_id: data.article_id.replace(/^https?:\/\//, ''), // Remove http/https prefix
            text: data.text || data.content || data.article_text // Get text from any of these fields
          };
          // console.log("Article text content:", formattedArticle.text);
          setCurrentArticle(formattedArticle);
          setError(null); // Clear any previous errors
        })
        .catch(error => {
          console.error("Error fetching article:", error);
          console.error("Error response:", error.response?.data);
          
          // Check if it's a specific backend error
          if (error.response?.data?.error === "Article not available due to privacy settings") {
            setError("Article not found");
          } else {
            setError("Article not found");
          }
        });

      // Fetch all feeds
      // getAllFeeds()
      //   .then(async (response) => {
      //     if (response && response.data && response.data.feeds) {
      //       // Process feeds to add thumbnail from last_article_url
      //       const processedFeeds = response.data.feeds.map(feed => {
      //         return {
      //           ...feed,
      //           feed_id: feed.feed_id || feed.id || `feed-${Math.random()}`,
      //           title: feed.title || feed.name || "Untitled Feed",
      //           date_added: feed.date_added || feed.created_at || new Date().toISOString(),
      //           article_id: feed.last_article_url || feed.feed_id || feed.id,
      //           audio_url: feed.audio_url || "https://audioread.com/share/placeholder.mp3",
      //         };
      //       });
            
      //       // Fetch latest article thumbnails for all feeds
      //       const feedsWithThumbnails = await Promise.all(
      //         processedFeeds.map(async (feed) => {
      //           try {
      //             if (feed.feed_id) {
      //               const latestArticle = await getLatestFeedArticleFromFeed(feed.feed_id);
                    
      //               if (latestArticle) {
      //                 return {
      //                   ...feed,
      //                   thumbnail_url: latestArticle.thumbnail_url || feed.thumbnail_url,
      //                   audio_url: latestArticle.audio_url,
      //                   article_id: latestArticle.article_id,
      //                   article_title: latestArticle.title,
      //                   article_url: latestArticle.url,
      //                   latestArticle: latestArticle
      //                 };
      //               }
      //             }
      //             return feed;
      //           } catch (error) {
      //             console.error(`Error fetching latest article for feed ${feed.feed_id}:`, error);
      //             return feed;
      //           }
      //         })
      //       );
            
      //       // Only take first 10 feeds
      //       setDisplayedFeeds(feedsWithThumbnails.slice(0, 10));
      //     }
      //     setFeedsLoading(false);
      //   })
      //   .catch(error => {
      //     console.error("Error fetching feeds:", error);
      //     setFeedsLoading(false);
      //   });
    }, [audio_id]);

    // Redirect to signin when trying to subscribe
    const handleSubscribe = () => {
      history.push('/signin');
    };

    // Handle play/pause button click
    const handlePlayPause = () => {
      console.log("Share page: Play/Pause button clicked, current state:", isPlaying);
      if (isPlaying) {
        console.log("Share page: Publishing pause event");
        publish("pause");
      } else {
        console.log("Share page: Publishing play event");
        publish("play");
      }
    };

    return (
      <>
        {currentArticle && !error && (
          <Helmet>
            <title>{currentArticle?.title ? `${currentArticle.title} - Listen to this article | Audioread` : 'Audioread'}</title>
            <meta name="description" content={`Listen to "${currentArticle?.title || 'this article'}" as a podcast. Convert any article to audio with Audioread. Perfect for listening on the go.`} />
            <meta name="keywords" content={`${currentArticle?.title || ''}, podcast, audio article, text to speech, listen to articles, audiobooks, Audioread`} />
            
            <meta property="og:title" content={`${currentArticle?.title || 'Article'} - Listen as a podcast`} />
            <meta property="og:description" content={`Listen to "${currentArticle?.title || 'this article'}" as a podcast. Convert any article to audio with Audioread.`} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:image" content={currentArticle?.thumbnail_url || "https://audioread.com/logo.png"} />
            
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={`${currentArticle?.title || 'Article'} - Listen as a podcast`} />
            <meta name="twitter:description" content={`Listen to "${currentArticle?.title || 'this article'}" as a podcast. Convert any article to audio with Audioread.`} />
            <meta name="twitter:image" content={currentArticle?.thumbnail_url || "https://audioread.com/logo.png"} />
            
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "AudioObject",
                "name": currentArticle?.title,
                "description": `Listen to "${currentArticle?.title || 'this article'}" as a podcast. Convert any article to audio with Audioread.`,
                "contentUrl": currentArticle?.audio_url,
                "thumbnailUrl": currentArticle?.thumbnail_url || "https://audioread.com/logo.png",
                "publisher": {
                  "@type": "Organization",
                  "name": "Audioread",
                  "url": "https://audioread.com"
                },
                "url": window.location.href,
                "datePublished": currentArticle?.published_at || new Date().toISOString(),
                "duration": currentArticle?.duration || "PT10M"
              })}
            </script>
          </Helmet>
        )}
        
        {error && (
          <Helmet>
            <title>Article Not Found | Audioread</title>
            <meta name="description" content="The article you're looking for could not be found or is no longer available." />
            <meta property="og:title" content="Article Not Found | Audioread" />
            <meta property="og:description" content="The article you're looking for could not be found or is no longer available." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:image" content="https://audioread.com/logo.png" />
          </Helmet>
        )}
        
        <div className="nav-bar">
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <Link to={`/${currentParams ? `?${currentParams}` : ''}`} style={{ textDecoration: 'none' }}>
              <img src={logo} className="audiblogs-logo" alt="logo" />
            </Link>
            <div style={{ marginLeft: '50px' }} className="ph-badge">
              <a
                href="https://www.producthunt.com/posts/audiblogs?utm_source=badge-top-post-badge&utm_medium=badge&utm_souce=badge-audiblogs"
                target="_blank"
              >
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=283759&theme=dark&period=monthly"
                  alt="Audioread - Listen to any web article in your podcast player | Product Hunt"
                  style={{
                    width: '250px',
                    height: '54px',
                  }}
                  width="250"
                  height="54"
                />
              </a>
            </div>
          </div>
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '1.05rem', fontWeight: 'bold' }}>
              <Link
                className="signin-button"
                to={`/feeds${currentParams ? `?${currentParams}` : ''}`}
              >
                Feeds
              </Link>
              <Link
                className="signin-button"
                to={{ pathname: "https://help.audioread.com/en/collections/12375780-use-cases" }}
                target="_blank"
              >
                Use Cases
              </Link>
              <Link
                className="signin-button"
                to={`/signin?source=share_nav_signin${currentParams ? `&${currentParams}` : ''}`}
              >
                Sign In
              </Link>
              <div style={{display: 'flex', alignItems: 'center', transform: 'scale(0.75)', marginLeft: '-20px'}}>
                <div className="cta-div" style={{ position: 'static', width: 'auto', paddingBottom: '18px' }}>
                  <Link
                    to={`/signin?source=share_nav_signup${currentParams ? `&${currentParams}` : ''}`}
                    className="cta-button"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                      <div>
                        <div>Start listening</div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PlayerWrapper showPlayer={true}>
          <main className="App-header" style={{
            minHeight: "calc(100vh - 86px)",
            display: "flex",
            flexDirection: "column",
            paddingTop: "60px"
          }}>
            {/* Error Display */}
            {error && (
              <div style={{
                maxWidth: "600px",
                margin: "0 auto",
                padding: "60px 20px",
                textAlign: "center"
              }}>
                <h1 style={{
                  fontSize: "2.5em",
                  color: "#fff",
                  marginBottom: "20px",
                  fontWeight: "600"
                }}>
                  {error}
                </h1>
                <p style={{
                  fontSize: "1.2em",
                  color: "rgba(255,255,255,0.8)",
                  marginBottom: "40px",
                  lineHeight: "1.6"
                }}>
                  The article you're looking for could not be found or is no longer available.
                </p>
                <Link
                  to={`/${currentParams ? `?${currentParams}` : ''}`}
                  style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    padding: '15px 30px',
                    borderRadius: '25px',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                  }}
                >
                  Go Home
                </Link>
              </div>
            )}

            {/* Loading State */}
            {!error && !currentArticle && (
              <Loader fullScreen={false} />
            )}

            {/* Main Article Content - Only show when no error and article is loaded */}
            {!error && currentArticle && (
              <article style={{ 
                maxWidth: "900px", 
                margin: "0 auto", 
                padding: "0 20px",
                textAlign: "left"
              }}>
              {/* Play Button - Prominent and Centered */}
              <div style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "30px",
                marginTop: window.innerWidth <= 768 ? "60px" : "20px"
              }}>
                <button
                  onClick={handlePlayPause}
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "scale(1.1)";
                    e.target.style.boxShadow = "0 12px 35px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.4)";
                  }}
                >
                  {/* Play/Pause Icon */}
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      marginLeft: isPlaying ? "0px" : "3px" // Adjust for play vs pause icon
                    }}
                  >
                    {isPlaying ? (
                      // Pause icon
                      <>
                        <rect x="6" y="4" width="4" height="16" fill="white" />
                        <rect x="14" y="4" width="4" height="16" fill="white" />
                      </>
                    ) : (
                      // Play icon
                      <path
                        d="M8 5.14V19.14L19 12.14L8 5.14Z"
                        fill="white"
                      />
                    )}
                  </svg>
                  
                  {/* Ripple effect */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: "100%",
                    height: "100%",
                    background: "radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)",
                    transform: "translate(-50%, -50%)",
                    animation: "pulse 2s ease-in-out infinite"
                  }} />
                </button>
                
                {/* Debug info - remove this in production */}
                {/* <div style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  background: "rgba(0,0,0,0.7)",
                  color: "white",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  fontSize: "12px",
                  zIndex: 1000
                }}>
                  State: {isPlaying ? "Playing" : "Paused"}
                </div> */}
              </div>

              {/* Article Header */}
              <div style={{
                marginBottom: "40px",
                textAlign: "center"
              }}>
                <h1
                  id="headline"
                  className="headline"
                  style={{
                    margin: "0 auto 25px auto",
                    maxWidth: "100%",
                    wordWrap: "break-word",
                    fontSize: "clamp(2em, 5vw, 3em)",
                    color: "#fff",
                    lineHeight: "1.2",
                    fontWeight: "700"
                  }}
                >
                  {currentArticle ? currentArticle.title : "Loading article..."}
                </h1>
                
                {/* Article Meta */}
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "20px",
                  flexWrap: "wrap",
                  marginBottom: "30px"
                }}>
                  <a
                    href={currentArticle ? `//${currentArticle.article_id}` : ""}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      color: "rgba(255,255,255,0.7)",
                      textDecoration: "underline",
                      fontSize: "14px",
                      transition: "color 0.2s ease"
                    }}
                    onMouseEnter={(e) => e.target.style.color = "#fff"}
                    onMouseLeave={(e) => e.target.style.color = "rgba(255,255,255,0.7)"}
                  >
                    View Original Article
                  </a>
                  
                  {/* Action Buttons */}
                  <div style={{ 
                    display: "flex", 
                    gap: "10px"
                  }}>
                    <button 
                      onClick={() => setIsShareModalOpen(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '20px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                        e.target.style.borderColor = 'rgba(255,255,255,0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                      }}
                    >
                      <ShareIcon style={{ fontSize: '16px' }} />
                      Share
                    </button>
                    <button 
                      onClick={() => setShowTranscript(!showTranscript)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '20px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                        e.target.style.borderColor = 'rgba(255,255,255,0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                      }}
                    >
                      {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Transcript Section */}
              {showTranscript && (
                <div style={{
                  marginBottom: '40px',
                  padding: '30px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.7',
                  fontSize: '16px',
                  maxHeight: '500px',
                  overflowY: 'auto'
                }}>
                  {currentArticle?.text ? currentArticle.text : "No transcript available"}
                </div>
              )}
              
              {/* Subtle CTA Section */}
              <div style={{
                marginTop: "60px",
                padding: "40px",
                background: "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                textAlign: "center",
                position: "relative",
                overflow: "hidden"
              }}>
                <div style={{
                  position: "absolute",
                  top: "-50%",
                  left: "-50%",
                  width: "200%",
                  height: "200%",
                  background: "radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)",
                  animation: "pulse 4s ease-in-out infinite"
                }} />
                <div style={{ position: "relative", zIndex: "1" }}>
                  <h3 style={{ 
                    margin: "0 0 15px 0", 
                    fontSize: "1.5em",
                    color: "#fff",
                    fontWeight: "600"
                  }}>
                    Want to listen to more articles like this?
                  </h3>
                  <p style={{ 
                    margin: "0 0 15px 0",
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "1.1em",
                    lineHeight: "1.5"
                  }}>
                    Convert any article, PDF, email, rss feed, and more into audio and listen in your favorite podcast app
                  </p>
                  <p style={{ 
                    margin: "0 0 25px 0",
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.9em",
                    fontWeight: "500"
                  }}>
                    ✨ Join 100,000+ audioreaders worldwide
                  </p>
                  <Link
                    to={`/signin?source=share_article_cta${currentParams ? `&${currentParams}` : ''}`}
                    style={{
                      display: 'inline-block',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      padding: '15px 30px',
                      borderRadius: '25px',
                      textDecoration: 'none',
                      fontSize: '16px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                    }}
                  >
                    Start Listening for Free
                  </Link>
                </div>
              </div>
            </article>
            )}
          </main>
        </PlayerWrapper>

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingBottom: '20px', marginBottom: '100px' }}>
          <OneGiftLogo />
          <div className="footer-links" style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
            <Link to={`/terms${currentParams ? `?${currentParams}` : ''}`}>Terms of Service</Link>
            <Link to={`/privacy${currentParams ? `?${currentParams}` : ''}`}>Privacy Policy</Link>
            <Link to={`/dmca${currentParams ? `?${currentParams}` : ''}`}>DMCA Policy</Link>
          </div>
          <div style={{ margin: '25px', opacity: 0.7 }}>© {new Date().getFullYear()} Audioread™. All rights reserved.</div>
        </div>

        <div className="share-player">
          {!error && currentArticle && (
            <Player
              {...{
                isFullPlayer,
                updateIsFullPlayer,
                currentArticle,
                onPreviousArticle,
                onNextArticle,
                onArticleEnded,
                listeningWhere: "/share",
                autoPlay: false,
                fullPlayerLeaveSpace: 0,
              }}
            />
          )}
        </div>

        {!error && currentArticle && (
          <ShareModal 
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            article={currentArticle}
          />
        )}
      </>
    );
  }
);

export default SharePlayer;
